import { useState, useEffect, useCallback } from "react";

import fetchWrapper from "../../lib/apiCall";
import { errorToast } from "../../util/toastNotifications";
import useAbortSignal from "../../hooks/useAbortSignal";

import calendarIcon from "../../assets/images/svg/calendar-icon.svg";
import { useTimeTracking } from "../../context/TimeTrackingContext";

function TimeEntryEditForm(props) {
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [showBillableMessage, setShowBillableMessage] = useState(false);
  const [editingRecord, setEditingRecord] = useState({
    editingStart: "",
    editingEnd: "",
    editingDate: "",
    editingIsBillable: false,
  });

  const { record, recordCheck, setIsEditModalOpen, fetchTimeEntries } = props;
  const { handleActivityData } = useTimeTracking();
  const { signal } = useAbortSignal();

  const handleTimeConversion = useCallback(
    (record_time, includeSeconds = false) => {
      if (recordCheck && record_time) {
        const date = new Date(record_time);

        let hour = date.getHours();
        let minute = date.getMinutes();
        let second = date.getSeconds();

        let time_of_day = "am";
        if (hour >= 12) {
          time_of_day = "pm";
          if (hour > 12) {
            hour = hour - 12;
          }
        } else if (hour === 0) {
          hour = 12;
        }

        const formattedHour = hour < 10 ? `0${hour}` : `${hour}`;
        const formattedMinute = minute < 10 ? `0${minute}` : `${minute}`;
        const formattedSecond = second < 10 ? `0${second}` : `${second}`;

        if (includeSeconds) {
          return `${formattedHour}:${formattedMinute}:${formattedSecond} ${time_of_day}`;
        } else {
          return `${formattedHour}:${formattedMinute} ${time_of_day}`;
        }
      }
    },
    [recordCheck]
  );

  const handleCheckboxChange = (e) => {
    setEditingRecord((prev) => ({
      ...prev,
      editingIsBillable: e.target.checked,
    }));

    setShowBillableMessage(!e.target.checked);
  };

  useEffect(() => {
    if (recordCheck && record) {
      const start = handleTimeConversion(record.date_started, true);
      const end = handleTimeConversion(record.date_ended, true);
      const date = record.date_started ? record.date_started.split("T")[0] : "";

      if (!editingRecord.editingStart)
        setEditingRecord((prev) => ({ ...prev, editingStart: start }));

      if (!editingRecord.editingEnd)
        setEditingRecord((prev) => ({ ...prev, editingEnd: end }));

      if (!editingRecord.editingDate)
        setEditingRecord((prev) => ({ ...prev, editingDate: date }));

      setEditingRecord((prev) => ({
        ...prev,
        editingIsBillable: record.billable,
      }));
    }
  }, [
    recordCheck,
    record?.date_started,
    record?.date_ended,
    record?.billable,
    editingRecord.editingStart,
    editingRecord.editingEnd,
    editingRecord.editingDate,
    handleTimeConversion,
  ]);

  useEffect(() => {
    if (isSavingEdit) {
      if (
        editingRecord.editingStart &&
        editingRecord.editingEnd &&
        editingRecord.editingDate
      ) {
        let editingRecordStart = editingRecord.editingStart;
        let editingRecordEnd = editingRecord.editingEnd;

        editingRecordStart = editingRecordStart.toLowerCase().trim();
        editingRecordEnd = editingRecordEnd.toLowerCase().trim();

        const validStartTimeOfDay =
          editingRecordStart.includes("am") ||
          editingRecordStart.includes("pm");
        const validEndTimeOfDay =
          editingRecordEnd.includes("am") || editingRecordEnd.includes("pm");

        let startDateTime = "";
        let endDateTime = "";

        let validStartHour = true;
        let validEndHour = true;
        let validStartMinute = true;
        let validEndMinute = true;
        let validTimeRange = true;

        if (validStartTimeOfDay && validEndTimeOfDay) {
          const startTimeOfDay = editingRecordStart.slice(-2).toLowerCase();
          const endTimeOfDay = editingRecordEnd.slice(-2).toLowerCase();

          const startTimeStr = editingRecordStart.slice(0, -2).trim();
          const endTimeStr = editingRecordEnd.slice(0, -2).trim();

          const startTimeParts = startTimeStr.split(":");
          const endTimeParts = endTimeStr.split(":");

          if (startTimeParts.length < 2 || endTimeParts.length < 2) {
            validStartHour = false;
            validEndHour = false;
          } else {
            const originalStartDate = record?.date_started
              ? new Date(record.date_started)
              : null;
            const originalEndDate = record?.date_ended
              ? new Date(record.date_ended)
              : null;
            const originalStartSecond = originalStartDate
              ? originalStartDate.getSeconds()
              : 0;
            const originalEndSecond = originalEndDate
              ? originalEndDate.getSeconds()
              : 0;

            let startHour = parseInt(startTimeParts[0], 10);
            let startMinute = parseInt(startTimeParts[1], 10);
            let startSecond =
              startTimeParts.length > 2
                ? parseInt(startTimeParts[2], 10)
                : originalStartSecond;

            let endHour = parseInt(endTimeParts[0], 10);
            let endMinute = parseInt(endTimeParts[1], 10);
            let endSecond =
              endTimeParts.length > 2
                ? parseInt(endTimeParts[2], 10)
                : originalEndSecond;

            if (startHour < 1 || startHour > 12) validStartHour = false;
            if (endHour < 1 || endHour > 12) validEndHour = false;

            if (startMinute < 0 || startMinute > 59) validStartMinute = false;
            if (endMinute < 0 || endMinute > 59) validEndMinute = false;

            if (startSecond < 0 || startSecond > 59) validStartMinute = false;
            if (endSecond < 0 || endSecond > 59) validEndMinute = false;

            if (startTimeParts[1].length === 1) validStartMinute = false;
            if (endTimeParts[1].length === 1) validEndMinute = false;

            if (startTimeOfDay === "pm" && startHour < 12) {
              startHour = startHour + 12;
            } else if (startTimeOfDay === "am" && startHour === 12) {
              startHour = 0;
            }

            if (endTimeOfDay === "pm" && endHour < 12) {
              endHour = endHour + 12;
            } else if (endTimeOfDay === "am" && endHour === 12) {
              endHour = 0;
            }

            const [year, month, day] = editingRecord.editingDate
              .split("-")
              .map(Number);
            const localStartDate = new Date(
              year,
              month - 1,
              day,
              startHour,
              startMinute,
              startSecond
            );
            const localEndDate = new Date(
              year,
              month - 1,
              day,
              endHour,
              endMinute,
              endSecond
            );

            startDateTime = localStartDate.toISOString();
            endDateTime = localEndDate.toISOString();

            const compareStart = new Date(startDateTime).getTime();
            const compareEnd = new Date(endDateTime).getTime();

            if (compareStart > compareEnd) validTimeRange = false;
          }
        }

        if (
          startDateTime &&
          endDateTime &&
          validStartHour &&
          validEndHour &&
          validStartMinute &&
          validEndMinute &&
          validTimeRange
        ) {
          const body = {
            org_id: record.org_id,
            user_id: record.user_id,
            entered_by_id: record.entered_by_id,
            date_started: startDateTime,
            date_ended: endDateTime,
            billable: editingRecord.editingIsBillable,
          };

          if (signal?.aborted) {
            setIsSavingEdit(false);
            return;
          }

          fetchWrapper
            .apiCall(
              `/time-entry/${record.time_entry_id}`,
              "PUT",
              body,
              null,
              signal
            )
            .then((data) => {
              if (data?.activity_log) {
                handleActivityData(data.activity_log);
              }
              fetchTimeEntries();
              setIsEditModalOpen(false);
              setIsSavingEdit(false);
            })
            .catch((err) => {
              if (
                err.name === "AbortError" ||
                err.message?.includes("aborted")
              ) {
                return;
              }
              console.error("Time Entry UPDATE error: ", err);
              errorToast("Failed to update time entry");
              setIsSavingEdit(false);
            });
        } else {
          errorToast("Invalid time input for time entry");
          setIsSavingEdit(false);
        }
      } else {
        setIsSavingEdit(false);
      }
    }
  }, [
    editingRecord,
    isSavingEdit,
    record.entered_by,
    record.org_id,
    record.time_entry_id,
    record.user_id,
    setIsEditModalOpen,
    handleActivityData,
  ]);

  useEffect(() => {
    return () => fetchTimeEntries();
  }, [fetchTimeEntries]);

  return (
    <div className="time-entry-edit-form-container">
      <div className="header-wrapper">
        <div className="header-assignee">Edit Time Entry</div>
      </div>

      <div className="time-record-edit-wrapper" key={record.time_entry_id}>
        <div className="time-record-edit-inner-wrapper">
          <div className="top-wrapper">
            <input
              className="time"
              type="text"
              name="start-time"
              maxLength={8}
              defaultValue={
                record?.date_started
                  ? handleTimeConversion(record.date_started)
                  : ""
              }
              onChange={(e) =>
                setEditingRecord((prev) => ({
                  ...prev,
                  editingStart: e.target.value,
                }))
              }
            />
            -
            <input
              className="time"
              type="text"
              name="end-time"
              maxLength={8}
              defaultValue={
                record?.date_ended
                  ? handleTimeConversion(record.date_ended)
                  : ""
              }
              onChange={(e) =>
                setEditingRecord((prev) => ({
                  ...prev,
                  editingEnd: e.target.value,
                }))
              }
            />
          </div>

          <div className="middle-wrapper">
            <div className="label-input-wrapper">
              <div className="date-input-wrapper">
                <input
                  className="date-input"
                  type="date"
                  defaultValue={
                    recordCheck && record?.date_started
                      ? record.date_started.split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setEditingRecord((prev) => ({
                      ...prev,
                      editingDate: e.target.value,
                    }))
                  }
                />
              </div>

              <img
                className="calendar-icon"
                src={calendarIcon}
                alt="calendar icon"
              />
            </div>
            <div className="checkbox-wrapper">
              <input
                className="checkbox"
                type="checkbox"
                id={`billable-checkbox-${record.time_entry_id}`}
                defaultChecked={record.billable}
                onChange={handleCheckboxChange}
              />

              <label
                className="checkbox-label"
                htmlFor={`billable-checkbox-${record.time_entry_id}`}
              >
                Is Billable
              </label>
            </div>
          </div>

          <div className="bottom-wrapper">
            {showBillableMessage ? (
              <div className="billable-message">
                This timestamp will save to{" "}
                <span className="highlight">Non-Billable</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="save-btn" onClick={() => setIsSavingEdit(true)}>
        Save
      </div>
    </div>
  );
}

export default TimeEntryEditForm;
